import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck, FileSignature, Camera, Scale, Home, Search, KeyRound, Wallet, RefreshCw, AlertCircle, Sparkles, Zap, Lock, BadgeCheck } from "lucide-react";
import Logo from "../components/Logo";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FOOMTicker from "../components/FOOMTicker";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";

const TENANT_IMG   = "https://res.cloudinary.com/dmq55juai/image/upload/v1780091496/tenant_photo.jpg";
const LANDLORD_IMG = "https://res.cloudinary.com/dmq55juai/image/upload/v1780090385/landlord_photo.jpg";
const BROKER_IMG   = "https://res.cloudinary.com/dmq55juai/image/upload/v1780091495/broker_photo.jpg";

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function FadeUp({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.8s ease ${delay}ms, transform 0.8s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  useEffect(() => { if (user) nav("/app"); }, [user, nav]);

  // ORE-AI carousel state
  const ORE_PHRASES = [
    { top: "Checks fair rent",      bottom: "for your exact area." },
    { top: "Move-in vs move-out.",  bottom: "Room by room." },
    { top: "Wear & tear",           bottom: "vs actual damage." },
    { top: "Instant verdict.",      bottom: "No courts needed." },
  ];
  const ORE_CHAT = [
    "Analysing evidence...",
    "Market rate here: ₹18/sqft",
    "Wall mark: pre-existing ✓",
    "Verdict ready →",
  ];
  const [oreIdx,  setOreIdx]  = useState(0);
  const [oreFade, setOreFade] = useState(true);
  const [chatIdx, setChatIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setOreFade(false);
      setTimeout(() => { setOreIdx(i => (i + 1) % ORE_PHRASES.length); setOreFade(true); }, 380);
    }, 3000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(() => setChatIdx(i => (i + 1) % ORE_CHAT.length), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#080808", color: "#F5F5F5" }}>

      <style>{`
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes pulse-ring { 0%{transform:scale(0.95);opacity:0.4} 100%{transform:scale(1.6);opacity:0} }
        @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        @keyframes glow-pulse { 0%,100%{opacity:0.4} 50%{opacity:0.8} }
        @keyframes ore-ping { 0%{transform:scale(1);opacity:0.55} 100%{transform:scale(1.6);opacity:0} }
        @keyframes ore-look {
          0%,18%  { transform: translate(0px, 0px); }
          25%,38% { transform: translate(-4px, 1px); }
          45%,58% { transform: translate(4px, -1px); }
          65%,78% { transform: translate(1px, 3px); }
          83%,85% { transform: translate(0px,0px) scaleY(0.07); }
          90%,100%{ transform: translate(0px, 0px); }
        }
        .ore-pupil   { animation: ore-look 5s ease-in-out infinite; }
        .ore-pupil-r { animation: ore-look 5s ease-in-out infinite 0.12s; }
        .float { animation: float 6s ease-in-out infinite; }
        .pulse-ring::after {
          content:''; position:absolute; inset:-12px; border-radius:50%;
          border:1px solid rgba(249,115,22,0.3);
          animation: pulse-ring 2.5s ease-out infinite;
        }
        .ticker-wrap { overflow:hidden; white-space:nowrap; }
        .ticker-inner { display:inline-flex; animation: ticker 28s linear infinite; }
        .ticker-inner:hover { animation-play-state:paused; }
        .glow-bg {
          position:absolute; width:600px; height:600px; border-radius:50%;
          background: radial-gradient(circle, rgba(249,115,22,0.12), transparent 70%);
          pointer-events:none; animation: glow-pulse 4s ease-in-out infinite;
        }
        .section-line { border:none; border-top:1px solid rgba(245,245,245,0.06); margin:0; }
        .proof-num {
          font-family:'Space Grotesk',sans-serif;
          font-size: clamp(4rem,8vw,7rem);
          font-weight:700; line-height:1;
          background: linear-gradient(135deg, #F97316, #FB923C);
          -webkit-background-clip:text; -webkit-text-fill-color:transparent;
          background-clip:text;
        }
      `}</style>

      {/* Navigation */}
      <Header />

      {/* Hero */}
      <section className="hero-glow dotted-grid relative overflow-hidden" style={{ minHeight: "92vh", display: "flex", alignItems: "center" }}>
        {/* dual gradient glow — orange + violet */}
        <div style={{
          position: "absolute", top: "5%", right: "-10%",
          width: 700, height: 700, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(249,115,22,0.12), transparent 70%)",
          pointerEvents: "none"
        }} />
        <div style={{
          position: "absolute", bottom: "5%", left: "-10%",
          width: 600, height: 600, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139,92,246,0.1), transparent 70%)",
          pointerEvents: "none"
        }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 w-full relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-start">

            {/* Left — headline */}
            <div className="fade-in-up">
              <div className="flex items-center gap-2 mb-8">
                <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
                <p className="text-sm font-medium tracking-widest uppercase" style={{
                  background: "linear-gradient(135deg, #F97316, #A78BFA)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}>
                  {t("hero_built_for")}
                </p>
              </div>
              <h1 className="font-display font-bold leading-none tracking-tight" data-testid="hero-headline"
                style={{ fontSize: "clamp(3.5rem, 9vw, 7rem)", color: "#F5F5F5" }}>
                {t("renting_without_fights")}<br />
                <span className="brand-gradient-text"></span>
              </h1>
              <p className="mt-10 text-xl font-light max-w-xl leading-relaxed" style={{ color: "#D1D5DB" }}>
                {t("hero_sub")}
              </p>
              <div className="mt-12 flex flex-wrap gap-4 items-center mb-12">
                <Link to="/register" data-testid="cta-hero-getstarted"
                  className="btn-press inline-flex items-center gap-2 font-medium px-7 py-4 rounded-full text-white"
                  style={{ background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)", fontSize: "1rem", boxShadow: "0 8px 32px rgba(139,92,246,0.25)" }}>
                  {t("hero_cta_get_started")} <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="#how" data-testid="cta-hero-howitworks"
                  className="btn-press inline-flex items-center gap-2 font-medium px-7 py-4 rounded-full"
                  style={{ border: "1px solid rgba(245,245,245,0.12)", color: "rgba(245,245,245,0.7)", fontSize: "1rem" }}>
                  {t("hero_cta_see_how")}
                </a>
              </div>

              {/* FOOM Ticker */}
              <FOOMTicker
                items={[
                  t("hero_ticker_setup"),
                  t("hero_ticker_deal"),
                  t("hero_ticker_disputes"),
                  t("hero_ticker_legal"),
                ]}
              />
            </div>

            {/* Right — 3-persona card */}
            <div className="float hidden md:block fade-in-up">
              <div style={{
                background: "#111111",
                border: "1px solid transparent",
                borderRadius: 24,
                padding: "2rem",
                position: "relative",
                backgroundImage: "linear-gradient(#111111, #111111), linear-gradient(135deg, rgba(249,115,22,0.4), rgba(139,92,246,0.4))",
                backgroundOrigin: "border-box",
                backgroundClip: "padding-box, border-box",
                boxShadow: "0 0 80px rgba(249,115,22,0.08), 0 0 80px rgba(139,92,246,0.08)",
              }}>
                {/* Header */}
                <div style={{ marginBottom: "1.5rem" }}>
                  <div className="text-xs font-medium tracking-widest uppercase mb-1" style={{ color: "#9CA3AF" }}>
                    Koramangala · 2BHK · Active deal
                  </div>
                  <div className="font-display font-bold" style={{ fontSize: "1.4rem", color: "#F5F5F5", lineHeight: 1.2 }}>
                    One platform.<br />Everyone protected.
                  </div>
                </div>

                {/* 3 persona rows */}
                {[
                  {
                    emoji: "🏠",
                    role: "Landlord",
                    name: "Meera R.",
                    detail: "Aadhaar-verified tenant · Agreement signed",
                    badge: "Documented ✓",
                    badgeColor: "#F97316",
                  },
                  {
                    emoji: "👤",
                    role: "Tenant",
                    name: "Arjun M.",
                    detail: "₹1,50,000 deposit held safe with Orello",
                    badge: "In Escrow ✓",
                    badgeColor: "#10B981",
                  },
                  {
                    emoji: "🤝",
                    role: "Broker",
                    name: "Imran K.",
                    detail: "Deal closed · ₹2,500 commission earned",
                    badge: "Paid Out ✓",
                    badgeColor: "#A78BFA",
                  },
                ].map((p, i) => (
                  <div key={p.role} style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "14px 16px",
                    borderRadius: 14,
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.07)",
                    marginBottom: i < 2 ? 10 : 0,
                  }}>
                    <div style={{ fontSize: "1.4rem", flexShrink: 0 }}>{p.emoji}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "#F5F5F5" }}>{p.role}</div>
                      <div style={{ fontSize: "0.7rem", color: "#6B7280", marginTop: 1 }}>{p.name}</div>
                      <div style={{ fontSize: "0.72rem", color: "#9CA3AF", marginTop: 2 }}>{p.detail}</div>
                    </div>
                    <span style={{
                      fontSize: "0.65rem", fontWeight: 700, flexShrink: 0,
                      padding: "4px 10px", borderRadius: 20,
                      background: `${p.badgeColor}18`,
                      color: p.badgeColor,
                      border: `1px solid ${p.badgeColor}30`,
                      whiteSpace: "nowrap",
                    }}>{p.badge}</span>
                  </div>
                ))}

                {/* Bottom status bar */}
                <div style={{
                  marginTop: 16, padding: "10px 14px", borderRadius: 10,
                  background: "linear-gradient(135deg, rgba(249,115,22,0.06), rgba(139,92,246,0.06))",
                  border: "1px solid rgba(249,115,22,0.12)",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <span style={{ fontSize: "0.75rem", color: "#D1D5DB" }}>Move-in report · Timestamped ·  Room-by-room</span>
                  <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "#F97316" }}>Locked ✓</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ORE-AI Section — second after hero */}
      <hr className="section-line" />
      <section style={{ background: "#111111" }} className="py-24 relative overflow-hidden">
        <div style={{
          position: "absolute", top: "10%", right: "-5%",
          width: 520, height: 520, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(249,115,22,0.07), transparent 70%)",
          pointerEvents: "none"
        }} />
        <div style={{
          position: "absolute", bottom: "5%", left: "-5%",
          width: 420, height: 420, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139,92,246,0.08), transparent 70%)",
          pointerEvents: "none"
        }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            {/* ── Left: Carousel text + mascot ── */}
            <FadeUp>
              <div style={{ display: "flex", flexDirection: "column", gap: "2.5rem" }}>
                {/* Large cycling text */}
                <div style={{ minHeight: 180, position: "relative" }}>
                  <div style={{
                    opacity: oreFade ? 1 : 0,
                    transform: oreFade ? "translateY(0)" : "translateY(14px)",
                    transition: "opacity 0.38s ease, transform 0.38s ease",
                  }}>
                    <div className="font-display font-bold leading-none" style={{
                      fontSize: "clamp(2rem, 3.5vw, 3.2rem)", color: "#F5F5F5", marginBottom: "0.15em",
                    }}>{ORE_PHRASES[oreIdx].top}</div>
                    <div className="font-display font-bold leading-none" style={{
                      fontSize: "clamp(2rem, 3.5vw, 3.2rem)",
                      background: "linear-gradient(135deg, #F97316, #8B5CF6)",
                      WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                    }}>{ORE_PHRASES[oreIdx].bottom}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: "1.5rem" }}>
                    {ORE_PHRASES.map((_, i) => (
                      <div key={i} style={{
                        height: 5, borderRadius: 3,
                        width: i === oreIdx ? 28 : 8,
                        background: i === oreIdx ? "linear-gradient(90deg, #F97316, #8B5CF6)" : "rgba(245,245,245,0.12)",
                        transition: "all 0.4s ease",
                      }} />
                    ))}
                  </div>
                </div>
                {/* Mascot + chat bubble */}
                <div style={{ display: "flex", alignItems: "flex-end", gap: 20 }}>
                  <div className="float" style={{ position: "relative", flexShrink: 0 }}>
                    <div style={{
                      position: "absolute", bottom: "100%", left: "50%",
                      transform: "translateX(-50%)", marginBottom: 10,
                      background: "#1a1a2e", border: "1px solid rgba(249,115,22,0.35)",
                      borderRadius: "14px 14px 14px 4px", padding: "8px 14px",
                      fontSize: "0.75rem", fontWeight: 600, color: "#F5F5F5",
                      whiteSpace: "nowrap", zIndex: 10,
                      boxShadow: "0 4px 20px rgba(249,115,22,0.12)",
                    }}>
                      <span style={{
                        background: "linear-gradient(135deg, #F97316, #A78BFA)",
                        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                      }}>{ORE_CHAT[chatIdx]}</span>
                      <div style={{
                        position: "absolute", bottom: -7, left: 14,
                        width: 0, height: 0,
                        borderLeft: "7px solid transparent", borderRight: "7px solid transparent",
                        borderTop: "8px solid rgba(249,115,22,0.35)",
                      }} />
                    </div>
                    <div style={{ position: "absolute", inset: -20, borderRadius: "50%", border: "1px solid rgba(249,115,22,0.15)", animation: "ore-ping 3s ease-out infinite" }} />
                    <div style={{ position: "absolute", inset: -10, borderRadius: "50%", border: "1px solid rgba(139,92,246,0.18)", animation: "ore-ping 3s ease-out infinite 1.1s" }} />
                    <svg width="240" height="270" viewBox="0 0 220 250" style={{ overflow: "visible" }}>
                      <defs>
                        <linearGradient id="oreGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#F97316" /><stop offset="100%" stopColor="#8B5CF6" />
                        </linearGradient>
                        <linearGradient id="oreGradH2" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#F97316" /><stop offset="100%" stopColor="#8B5CF6" />
                        </linearGradient>
                        <filter id="oreGlowF2" x="-30%" y="-30%" width="160%" height="160%">
                          <feGaussianBlur in="SourceGraphic" stdDeviation="7" result="blur" />
                        </filter>
                        <clipPath id="oreInnerClip2"><circle cx="110" cy="128" r="73" /></clipPath>
                      </defs>
                      <circle cx="110" cy="128" r="90" fill="none" stroke="url(#oreGrad2)" strokeWidth="28" filter="url(#oreGlowF2)" opacity="0.2" />
                      <circle cx="110" cy="128" r="88" fill="none" stroke="url(#oreGrad2)" strokeWidth="18" />
                      <circle cx="110" cy="128" r="79" fill="#111111" />
                      <g clipPath="url(#oreInnerClip2)">
                        <rect x="37" y="0" width="146" height="2.5" fill="url(#oreGradH2)" opacity="0.4">
                          <animateTransform attributeName="transform" type="translate" values="0,52;0,178;0,52" dur="2.8s" repeatCount="indefinite" />
                        </rect>
                      </g>
                      <ellipse cx="82" cy="126" rx="13" ry="14" fill="white" />
                      <g className="ore-pupil">
                        <circle cx="85" cy="128" r="8" fill="#0d0d0d" />
                        <circle cx="83" cy="125" r="4" fill="url(#oreGrad2)" />
                        <circle cx="81" cy="123" r="2" fill="white" opacity="0.85" />
                      </g>
                      <ellipse cx="138" cy="126" rx="13" ry="14" fill="white" />
                      <g className="ore-pupil-r">
                        <circle cx="141" cy="128" r="8" fill="#0d0d0d" />
                        <circle cx="139" cy="125" r="4" fill="url(#oreGrad2)" />
                        <circle cx="137" cy="123" r="2" fill="white" opacity="0.85" />
                      </g>
                      <line x1="110" y1="40" x2="110" y2="20" stroke="url(#oreGrad2)" strokeWidth="3" strokeLinecap="round" />
                      <circle cx="110" cy="15" r="5.5" fill="#F97316" />
                      <circle cx="110" cy="15" r="5.5" fill="none" stroke="#F97316" strokeWidth="1.5" opacity="0">
                        <animate attributeName="r" values="5.5;13;5.5" dur="1.6s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.5;0;0.5" dur="1.6s" repeatCount="indefinite" />
                      </circle>
                      <circle cx="20" cy="115" r="3" fill="#F97316" opacity="0.4" />
                      <line x1="23" y1="115" x2="34" y2="115" stroke="#F97316" strokeWidth="1.2" opacity="0.2" />
                      <circle cx="200" cy="115" r="3" fill="#8B5CF6" opacity="0.4" />
                      <line x1="197" y1="115" x2="186" y2="115" stroke="#8B5CF6" strokeWidth="1.2" opacity="0.2" />
                      <rect x="68" y="226" width="84" height="22" rx="11" fill="none" stroke="url(#oreGrad2)" strokeWidth="1" opacity="0.5" />
                      <text x="110" y="241" textAnchor="middle" fill="url(#oreGrad2)" fontSize="9" fontWeight="800" letterSpacing="3" fontFamily="'Space Grotesk', sans-serif">ORE-AI</text>
                    </svg>
                  </div>
                  <div style={{ paddingBottom: "1.5rem" }}>
                    <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.15em", color: "#F97316", textTransform: "uppercase", marginBottom: 6 }}>ORE-AI</div>
                    <p style={{ fontSize: "0.85rem", color: "#9CA3AF", lineHeight: 1.6, maxWidth: 180 }}>
                      Neutral. Evidence-based. Fair to every party in the deal.
                    </p>
                  </div>
                </div>
              </div>
            </FadeUp>
            {/* ── Right: Content ── */}
            <div style={{ paddingTop: "1rem" }}>
              <FadeUp>
                <div className="flex items-center gap-2 mb-5">
                  <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
                  <p className="text-xs tracking-widest uppercase" style={{
                    background: "linear-gradient(135deg, #F97316, #A78BFA)",
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                  }}>Meet ORE-AI</p>
                </div>
                <h2 className="font-display font-bold leading-tight mb-5"
                  style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "#F5F5F5" }}>
                  India's first AI built<br />
                  <span style={{
                    background: "linear-gradient(135deg, #F97316, #8B5CF6)",
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                  }}>for rental disputes.</span>
                </h2>
                <p className="font-light leading-relaxed mb-10" style={{ fontSize: "1rem", color: "#D1D5DB", maxWidth: 480 }}>
                  When a tenant and landlord disagree at move-out, ORE-AI steps in. It reviews the evidence, cross-checks condition reports, and delivers a binding verdict — without courts or lawyers.
                </p>
              </FadeUp>
              <div style={{ borderTop: "1px solid rgba(245,245,245,0.07)" }}>
                {[
                  { n: "01", title: "Evidence submitted", desc: "Both parties upload photos, notes, and condition reports through Orello. Everything is timestamped and tamper-proof." },
                  { n: "02", title: "ORE-AI analyses", desc: "Cross-references move-in photos, checkout damage, and local rental market rates to assess every claim objectively." },
                  { n: "03", title: "Instant binding verdict", desc: "Evidence-based decision delivered immediately. No lawyers. No courtrooms. Both sides accept the outcome." },
                ].map((s, i) => (
                  <FadeUp key={s.n} delay={i * 100}>
                    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", padding: "1.5rem 0", borderBottom: "1px solid rgba(245,245,245,0.07)" }}>
                      <div style={{
                        minWidth: 38, height: 38, borderRadius: "50%",
                        background: "linear-gradient(135deg, rgba(249,115,22,0.12), rgba(139,92,246,0.12))",
                        border: "1px solid rgba(249,115,22,0.25)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "0.72rem", fontWeight: 800, color: "#F97316", flexShrink: 0,
                      }}>{s.n}</div>
                      <div>
                        <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "#F5F5F5", marginBottom: 4 }}>{s.title}</div>
                        <p style={{ fontSize: "0.85rem", color: "#9CA3AF", lineHeight: 1.65 }}>{s.desc}</p>
                      </div>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ticker — scrolling trust bar */}
      <div className="ticker-wrap py-5" style={{ background: "#F97316" }}>
        <div className="ticker-inner">
          {[...Array(2)].map((_, i) => (
            <span key={i} className="inline-flex items-center">
              {[
                t("ticker_escrow"),
                t("ticker_legal"),
                t("ticker_aadhaar"),
                t("ticker_condition"),
                t("ticker_dispute"),
                t("ticker_nowhatsapp"),
                t("ticker_3way"),
                t("ticker_india"),
              ].map((item) => (
                <span key={item} className="mx-8 text-sm font-medium tracking-wide uppercase text-white opacity-90">
                  {item} <span className="mx-4 opacity-40">·</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* Problem */}
      <hr className="section-line" />
      <section style={{ background: "#111111" }} className="py-32 relative overflow-hidden">
        <div className="glow-bg" style={{ top: "10%", right: "5%", opacity: 0.6 }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <FadeUp>
            <div className="flex items-center gap-2 mb-6">
              <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
              <p className="text-sm font-semibold tracking-widest uppercase" style={{
                background: "linear-gradient(135deg, #F97316, #A78BFA)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>{t("problem_label")}</p>
            </div>
            <h2 className="font-display font-bold leading-tight mb-20"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", color: "#F5F5F5" }}
              data-testid="problem-title">
              {t("problem_heading")}
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                n: "01",
                icon: FileSignature,
                title: t("problem_01_title"),
                q: t("problem_01_body"),
                tag: t("problem_01_tag"),
                accent: "#F97316",
              },
              {
                n: "02",
                icon: Camera,
                title: t("problem_02_title"),
                q: t("problem_02_body"),
                tag: t("problem_02_tag"),
                accent: "gradient",
              },
              {
                n: "03",
                icon: ShieldCheck,
                title: t("problem_03_title"),
                q: t("problem_03_body"),
                tag: t("problem_03_tag"),
                accent: "#8B5CF6",
              },
            ].map((item, i) => {
              const isGradient = item.accent === "gradient";
              const accent = isGradient ? "#F97316" : item.accent;
              const iconBg = isGradient
                ? "linear-gradient(135deg, #F97316, #8B5CF6)"
                : `${accent}1A`;
              const iconColor = isGradient ? "#fff" : accent;
              const stepColor = isGradient ? "#FB923C" : accent;
              const cardBorder = isGradient
                ? "rgba(249,115,22,0.15)"
                : `${accent}33`;
              const cardGlow = isGradient
                ? "radial-gradient(circle, rgba(168,85,247,0.06), transparent 70%)"
                : item.accent === "#F97316"
                ? "radial-gradient(circle, rgba(249,115,22,0.08), transparent 70%)"
                : "radial-gradient(circle, rgba(139,92,246,0.08), transparent 70%)";

              return (
                <FadeUp key={item.n} delay={i * 120}>
                  <div style={{
                    padding: "2.5rem",
                    borderRadius: 20,
                    background: "#0B0B14",
                    border: `1px solid ${cardBorder}`,
                    height: "100%",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.25rem",
                  }}>
                    {/* corner glow */}
                    <div style={{
                      position: "absolute", bottom: -60, right: -60,
                      width: 200, height: 200, borderRadius: "50%",
                      background: cardGlow,
                      pointerEvents: "none"
                    }} />

                    {/* Icon badge */}
                    <div style={{
                      width: 64, height: 64, borderRadius: 16,
                      background: iconBg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                      position: "relative",
                    }}>
                      <item.icon style={{ width: 30, height: 30, color: iconColor }} />
                    </div>

                    {/* Step label */}
                    <div className="font-display font-bold" style={{
                      fontSize: "0.75rem",
                      letterSpacing: "0.2em",
                      color: stepColor,
                      position: "relative",
                    }}>PROBLEM {item.n}</div>

                    {/* title */}
                    <div className="font-display font-bold" style={{
                      fontSize: "1.4rem",
                      color: "#F5F5F5",
                      lineHeight: 1.2,
                      position: "relative",
                    }}>{item.title}</div>

                    {/* body */}
                    <p className="font-light leading-relaxed" style={{
                      fontSize: "0.95rem",
                      color: "#D1D5DB",
                      flex: 1,
                      position: "relative",
                    }}>{item.q}</p>

                    {/* tag pill */}
                    <div style={{
                      display: "inline-flex", alignItems: "center",
                      padding: "6px 14px", borderRadius: 100,
                      background: isGradient
                        ? "linear-gradient(135deg, rgba(249,115,22,0.12), rgba(139,92,246,0.12))"
                        : `${accent}1A`,
                      border: `1px solid ${accent}33`,
                      fontSize: "0.8rem", fontWeight: 600,
                      color: stepColor,
                      alignSelf: "flex-start",
                      position: "relative",
                    }}>{item.tag}</div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* The Renting Cycle — full protection lifecycle */}
      <hr className="section-line" />
      <section id="how" className="py-32 relative overflow-hidden" style={{ background: "#080808" }}>
        {/* dual glow — orange + violet */}
        <div style={{
          position: "absolute", top: "20%", left: "10%",
          width: 500, height: 500, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(249,115,22,0.08), transparent 70%)",
          pointerEvents: "none"
        }} />
        <div style={{
          position: "absolute", bottom: "10%", right: "10%",
          width: 500, height: 500, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139,92,246,0.08), transparent 70%)",
          pointerEvents: "none"
        }} />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <FadeUp>
            <div className="flex items-center gap-2 mb-6">
              <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
              <p className="text-xs tracking-widest uppercase" style={{ color: "#A78BFA" }}>{t("how_label")}</p>
            </div>
            <h2 className="font-display font-bold leading-tight mb-6"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)", color: "#F5F5F5" }}>
              {t("how_heading_1")}
              <br />
              <span className="brand-gradient-text">{t("how_heading_2")}</span>
            </h2>
            <p className="font-light mb-20 max-w-2xl" style={{ fontSize: "1.05rem", color: "#D1D5DB", lineHeight: 1.7 }}>
              {t("how_body")}
            </p>
          </FadeUp>

          {/* Vertical flow with connecting line */}
          <div className="relative">
            {/* connecting gradient line */}
            <div className="hidden md:block" style={{
              position: "absolute", left: "50%", top: 0, bottom: 0,
              width: 2, transform: "translateX(-50%)",
              background: "linear-gradient(to bottom, #F97316 0%, #F97316 30%, #8B5CF6 70%, #8B5CF6 100%)",
              opacity: 0.3,
            }} />

            {[
              {
                i: Search, n: "01", t: t("how_step_01_title"),
                d: t("how_step_01_desc"),
                accent: "orange", side: "left",
              },
              {
                i: FileSignature, n: "02", t: t("how_step_02_title"),
                d: t("how_step_02_desc"),
                accent: "orange", side: "right",
              },
              {
                i: Lock, n: "03", t: t("how_step_03_title"),
                d: t("how_step_03_desc"),
                accent: "gradient", side: "left",
              },
              {
                i: Camera, n: "04", t: t("how_step_04_title"),
                d: t("how_step_04_desc"),
                accent: "gradient", side: "right",
              },
              {
                i: Home, n: "05", t: t("how_step_05_title"),
                d: t("how_step_05_desc"),
                accent: "violet", side: "left",
              },
              {
                i: RefreshCw, n: "06", t: t("how_step_06_title"),
                d: t("how_step_06_desc"),
                accent: "violet", side: "right",
              },
            ].map((s, i) => {
              const accentColor = s.accent === "orange" ? "#F97316" : s.accent === "violet" ? "#8B5CF6" : "linear-gradient(135deg, #F97316, #8B5CF6)";
              const borderColor = s.accent === "orange" ? "rgba(249,115,22,0.2)" : s.accent === "violet" ? "rgba(139,92,246,0.2)" : "rgba(249,115,22,0.15)";
              const isLeft = s.side === "left";

              return (
                <FadeUp key={s.n} delay={i * 80}>
                  <div className="grid md:grid-cols-2 gap-8 mb-3 items-center">
                    {/* Card content (alternating sides on desktop) */}
                    <div className={isLeft ? "md:order-1" : "md:order-2"}>
                      <div style={{
                        padding: "2.5rem",
                        borderRadius: 20,
                        background: "#111111",
                        border: `1px solid ${borderColor}`,
                        position: "relative",
                        overflow: "hidden",
                      }}>
                        {/* per-card glow */}
                        <div style={{
                          position: "absolute", bottom: -60, right: -60,
                          width: 200, height: 200, borderRadius: "50%",
                          background: s.accent === "orange"
                            ? "radial-gradient(circle, rgba(249,115,22,0.08), transparent 70%)"
                            : s.accent === "violet"
                            ? "radial-gradient(circle, rgba(139,92,246,0.08), transparent 70%)"
                            : "radial-gradient(circle, rgba(168,85,247,0.06), transparent 70%)",
                          pointerEvents: "none"
                        }} />
                        <div className="flex items-start gap-5 relative">
                          {/* Icon badge with gradient or solid */}
                          <div style={{
                            width: 64, height: 64, borderRadius: 16,
                            background: s.accent === "gradient"
                              ? "linear-gradient(135deg, #F97316, #8B5CF6)"
                              : s.accent === "orange"
                              ? "rgba(249,115,22,0.12)"
                              : "rgba(139,92,246,0.12)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                          }}>
                            <s.i style={{
                              width: 30, height: 30,
                              color: s.accent === "gradient" ? "#fff" : s.accent === "orange" ? "#F97316" : "#A78BFA"
                            }} />
                          </div>
                          <div>
                            <div className="font-display font-bold mb-2" style={{
                              fontSize: "0.75rem",
                              letterSpacing: "0.2em",
                              color: s.accent === "orange" ? "#F97316" : s.accent === "violet" ? "#A78BFA" : "#FB923C",
                            }}>STEP {s.n}</div>
                            <h3 className="font-display font-bold mb-3" style={{ fontSize: "1.5rem", color: "#F5F5F5", lineHeight: 1.2 }}>{s.t}</h3>
                            <p className="font-light" style={{ fontSize: "0.95rem", color: "#D1D5DB", lineHeight: 1.7 }}>{s.d}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Dot on the line (visible only on desktop) */}
                    <div className={`hidden md:flex ${isLeft ? "md:order-2 md:justify-start" : "md:order-1 md:justify-end"} relative`}>
                      <div className="hidden md:block" style={{
                        position: "absolute",
                        [isLeft ? "left" : "right"]: "-10%",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 16, height: 16, borderRadius: "50%",
                        background: s.accent === "gradient" ? "linear-gradient(135deg, #F97316, #8B5CF6)" : s.accent === "orange" ? "#F97316" : "#8B5CF6",
                        boxShadow: s.accent === "violet" ? "0 0 24px rgba(139,92,246,0.6)" : "0 0 24px rgba(249,115,22,0.6)",
                      }} />
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>

          {/* Bottom CTA pill */}
          <FadeUp delay={500}>
            <div className="mt-16 text-center">
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 12,
                padding: "16px 28px", borderRadius: 999,
                background: "linear-gradient(135deg, rgba(249,115,22,0.1), rgba(139,92,246,0.1))",
                border: "1px solid rgba(249,115,22,0.2)",
              }}>
                <Sparkles style={{ width: 18, height: 18, color: "#F97316" }} />
                <p style={{ fontSize: "0.95rem", color: "#F5F5F5" }}>
                  {t("how_bottom_pill")}
                </p>
                <BadgeCheck style={{ width: 18, height: 18, color: "#A78BFA" }} />
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Why Orello — Benefits grid */}
      <hr className="section-line" />
      <section className="py-32 relative overflow-hidden" style={{ background: "#0B0B14" }}>
        <div style={{
          position: "absolute", top: "30%", right: "5%",
          width: 600, height: 600, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139,92,246,0.06), transparent 70%)",
          pointerEvents: "none"
        }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <FadeUp>
            <div className="flex items-center gap-2 mb-6">
              <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
              <p className="text-xs tracking-widest uppercase" style={{ color: "#F97316" }}>{t("why_label")}</p>
            </div>
            <h2 className="font-display font-bold leading-tight mb-16"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", color: "#F5F5F5" }}>
              {t("why_heading_1")}<br />
              {t("why_heading_2")}
            </h2>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { i: Lock, t: t("why_b1_title"), d: t("why_b1_desc"), color: "#F97316" },
              { i: Zap, t: t("why_b2_title"), d: t("why_b2_desc"), color: "#FB923C" },
              { i: Scale, t: t("why_b3_title"), d: t("why_b3_desc"), color: "#A78BFA" },
              { i: BadgeCheck, t: t("why_b4_title"), d: t("why_b4_desc"), color: "#8B5CF6" },
              { i: Camera, t: t("why_b5_title"), d: t("why_b5_desc"), color: "#F97316" },
              { i: Wallet, t: t("why_b6_title"), d: t("why_b6_desc"), color: "#A78BFA" },
            ].map((b, i) => (
              <FadeUp key={i} delay={i * 60}>
                <div style={{
                  padding: "2rem",
                  borderRadius: 16,
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  height: "100%",
                  transition: "all 0.3s ease",
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${b.color}40`; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: `${b.color}1A`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    marginBottom: "1.25rem"
                  }}>
                    <b.i style={{ width: 24, height: 24, color: b.color }} />
                  </div>
                  <h3 className="font-display font-semibold mb-2" style={{ fontSize: "1.15rem", color: "#F5F5F5" }}>{b.t}</h3>
                  <p className="font-light" style={{ fontSize: "0.9rem", color: "#D1D5DB", lineHeight: 1.6 }}>{b.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Persona sections — per user, with key features */}
      <hr className="section-line" />
      <section id="who">
        {[
          {
            img: TENANT_IMG,
            label: t("persona_tenant_label"),
            headline: t("persona_tenant_headline"),
            body: t("persona_tenant_body"),
            features: [
              { n: "01", t: t("persona_tenant_f1_t"), d: t("persona_tenant_f1_d") },
              { n: "02", t: t("persona_tenant_f2_t"), d: t("persona_tenant_f2_d") },
              { n: "03", t: t("persona_tenant_f3_t"), d: t("persona_tenant_f3_d") },
              { n: "04", t: t("persona_tenant_f4_t"), d: t("persona_tenant_f4_d") },
            ],
            cta: t("persona_tenant_cta"),
          },
          {
            img: LANDLORD_IMG,
            label: t("persona_landlord_label"),
            headline: t("persona_landlord_headline"),
            body: t("persona_landlord_body"),
            features: [
              { n: "01", t: t("persona_landlord_f1_t"), d: t("persona_landlord_f1_d") },
              { n: "02", t: t("persona_landlord_f2_t"), d: t("persona_landlord_f2_d") },
              { n: "03", t: t("persona_landlord_f3_t"), d: t("persona_landlord_f3_d") },
              { n: "04", t: t("persona_landlord_f4_t"), d: t("persona_landlord_f4_d") },
            ],
            cta: t("persona_landlord_cta"),
            reverse: true,
          },
          {
            img: BROKER_IMG,
            label: t("persona_broker_label"),
            headline: t("persona_broker_headline"),
            body: t("persona_broker_body"),
            features: [
              { n: "01", t: t("persona_broker_f1_t"), d: t("persona_broker_f1_d") },
              { n: "02", t: t("persona_broker_f2_t"), d: t("persona_broker_f2_d") },
              { n: "03", t: t("persona_broker_f3_t"), d: t("persona_broker_f3_d") },
              { n: "04", t: t("persona_broker_f4_t"), d: t("persona_broker_f4_d") },
            ],
            cta: t("persona_broker_cta"),
          },
        ].map((p, i) => (
          <div key={i}>
            <hr className="section-line" />
            <div className={`grid md:grid-cols-2`} style={{ background: i % 2 === 0 ? "#111111" : "#080808" }}>
              {/* Image side */}
              <div className={`relative overflow-hidden ${p.reverse ? "md:order-2" : ""}`} style={{ minHeight: 520 }}>
                <img src={p.img} alt={p.label} className="w-full h-full object-cover absolute inset-0"
                  style={{ filter: "brightness(0.35)" }} />
                <div className="absolute inset-0" style={{
                  background: p.reverse
                    ? "linear-gradient(to left, rgba(8,8,8,0.5), transparent)"
                    : "linear-gradient(to right, rgba(8,8,8,0.5), transparent)"
                }} />
                {/* Big label on image */}
                <div className="absolute bottom-10 left-10">
                  <p className="font-display font-bold leading-none" style={{ fontSize: "clamp(3rem, 5vw, 5rem)", color: "rgba(249,115,22,0.15)", letterSpacing: "-0.03em" }}>
                    {p.label.split(" ").pop()}
                  </p>
                </div>
              </div>

              {/* Content side */}
              <div className={`flex flex-col justify-center px-12 py-20 ${p.reverse ? "md:order-1" : ""}`}>
                <FadeUp>
                  <div className="flex items-center gap-2 mb-6">
                    <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
                    <p className="text-xs tracking-widest uppercase" style={{
                      background: "linear-gradient(135deg, #F97316, #A78BFA)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}>{p.label}</p>
                  </div>
                  <h3 className="font-display font-bold leading-tight mb-6"
                    style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", color: "#F5F5F5", whiteSpace: "pre-line" }}>
                    {p.headline}
                  </h3>
                  <p className="text-base font-light leading-relaxed mb-12" style={{ color: "#D1D5DB", maxWidth: 400 }}>
                    {p.body}
                  </p>
                </FadeUp>

                {/* Numbered features */}
                <div className="space-y-0" style={{ borderTop: "1px solid rgba(245,245,245,0.06)" }}>
                  {p.features.map((f, fi) => (
                    <FadeUp key={f.n} delay={fi * 100}>
                      <div className="py-6 grid grid-cols-12 gap-4 items-center"
                        style={{ borderBottom: "1px solid rgba(245,245,245,0.06)" }}>
                        <div className="col-span-1 font-display font-bold" style={{ fontSize: "clamp(1.5rem, 2.5vw, 2.2rem)", lineHeight: 1, color: "rgba(249,115,22,0.6)" }}>{f.n}</div>
                        <div className="col-span-4">
                          <div className="font-semibold font-display" style={{ fontSize: "1rem", color: "#F5F5F5" }}>{f.t}</div>
                        </div>
                        <div className="col-span-7">
                          <p className="text-sm font-light leading-relaxed" style={{ color: "#D1D5DB" }}>{f.d}</p>
                        </div>
                      </div>
                    </FadeUp>
                  ))}
                </div>

                <FadeUp delay={350}>
                  <Link to="/register" className="btn-press inline-flex items-center gap-2 text-sm font-medium mt-10"
                    style={{ color: "#F97316" }}>
                    {p.cta} <ArrowRight className="h-4 w-4" />
                  </Link>
                </FadeUp>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Proof section — CRED style */}
      <hr className="section-line" />
      <section style={{ background: "#111111" }} className="py-32 relative overflow-hidden">
        <div className="glow-bg" style={{ bottom: "-20%", left: "30%" }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <FadeUp>
            <div className="flex items-center gap-2 mb-6">
              <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
              <p className="text-xs tracking-widest uppercase" style={{
                background: "linear-gradient(135deg, #F97316, #A78BFA)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>{t("proof_label")}</p>
            </div>
            <h2 className="font-display font-bold leading-tight mb-20"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", color: "#F5F5F5" }}>
              {t("proof_heading_1")}<br />{t("proof_heading_2")}
            </h2>
          </FadeUp>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-px" style={{ background: "rgba(245,245,245,0.06)" }}>
            {[
              { stat: "₹0",     label: t("proof_stat1_label"), sub: t("proof_stat1_sub") },
              { stat: "5 min",  label: t("proof_stat2_label"), sub: t("proof_stat2_sub") },
              { stat: "10 days",label: t("proof_stat3_label"), sub: t("proof_stat3_sub") },
              { stat: "100%",   label: t("proof_stat4_label"), sub: t("proof_stat4_sub") },
              { stat: "day 1",  label: t("proof_stat5_label"), sub: t("proof_stat5_sub") },
            ].map((s, i) => (
              <FadeUp key={s.stat} delay={i * 80}>
                <div className="px-6 py-8 md:px-8 md:py-10" style={{ background: "#111111" }}>
                  <div className="proof-num mb-2" style={{ fontSize: "clamp(1.8rem, 3.5vw, 3.8rem)" }}>{s.stat}</div>
                  <div className="font-display font-semibold mb-1" style={{ fontSize: "0.9rem", color: "#F5F5F5" }}>{s.label}</div>
                  <p className="font-light" style={{ fontSize: "0.78rem", color: "#9CA3AF" }}>{s.sub}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Stats section */}
      <hr className="section-line" />
      <section style={{ background: "#0a0a0a" }} className="py-24 md:py-32 relative overflow-hidden">
        <div style={{
          position: "absolute", top: "10%", right: "15%",
          width: 600, height: 600, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(249,115,22,0.07), transparent 70%)",
          pointerEvents: "none"
        }} />
        <div style={{
          position: "absolute", bottom: "10%", left: "5%",
          width: 500, height: 500, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139,92,246,0.06), transparent 70%)",
          pointerEvents: "none"
        }} />
        {/* Tagline */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <FadeUp>
            <h2 className="font-display font-bold leading-tight mb-16 text-center"
              style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", color: "#F5F5F5" }}>
              Built for trust.<br />
              <span style={{
                background: "linear-gradient(135deg, #F97316, #8B5CF6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>Backed by the numbers.</span>
            </h2>
          </FadeUp>
        </div>

        {/* Stats Row — full width so tiles spread evenly */}
        <FadeUp delay={120}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px relative z-10" style={{ background: "rgba(245,245,245,0.06)" }}>
            {[
              { num: "98%",    label: t("fomo_stat1_label") },
              { num: "Instant", label: t("fomo_stat2_label") },
              { num: "100%",   label: t("fomo_stat3_label") },
              { num: "0₹",     label: t("fomo_stat4_label") },
            ].map((s, i) => (
              <div key={i} className="px-8 py-12 md:px-14 md:py-16" style={{ background: "#0a0a0a" }}>
                <div className="font-display font-bold mb-3" style={{
                  fontSize: "clamp(2.8rem, 5.5vw, 5rem)",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  background: "linear-gradient(135deg, #F97316, #8B5CF6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}>{s.num}</div>
                <p style={{ fontSize: "0.95rem", color: "#D1D5DB", lineHeight: 1.5 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </FadeUp>

        {/* Review cards — hidden until real reviews are collected */}
      </section>

      {/* Broker CTA */}
      <hr className="section-line" />
      <section id="brokers" style={{ background: "#080808" }} className="py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <FadeUp>
            <div className="flex items-center gap-2 mb-6">
              <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
              <p className="text-xs tracking-widest uppercase" style={{
                background: "linear-gradient(135deg, #F97316, #A78BFA)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>{t("broker_cta_label")}</p>
            </div>
            <h2 className="font-display font-bold leading-tight mb-6"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", color: "#F5F5F5" }}>
              {t("broker_cta_heading_1")}<br />
              <span className="brand-gradient-text">{t("broker_cta_heading_2")}</span>
            </h2>
            <p className="text-lg font-light leading-relaxed mb-16 max-w-2xl" style={{ color: "#D1D5DB" }}>
              {t("broker_cta_body")}
            </p>
          </FadeUp>
          <Link to="/register" data-testid="cta-broker-signup"
            className="btn-press inline-flex items-center gap-2 font-medium px-7 py-4 rounded-full text-white"
            style={{ background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)", fontSize: "1rem", boxShadow: "0 8px 32px rgba(139,92,246,0.25)" }}>
            {t("broker_cta_btn")} <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-4 text-xs" style={{ color: "#6B7280" }}>{t("broker_cta_fine")}</p>
        </div>
      </section>

      {/* Final CTA */}
      <hr className="section-line" />
      <section style={{ background: "#111111" }} className="py-40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <FadeUp>
            <h2 className="font-display font-bold leading-tight mb-6"
              style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", color: "#F5F5F5" }}>
              {t("final_cta_heading_1")}<br />
              <span className="brand-gradient-text">{t("final_cta_heading_2")}</span>
            </h2>
            <p className="text-lg font-light mb-12" style={{ color: "#D1D5DB" }}>
              {t("final_cta_body")}
            </p>
            <Link to="/register" data-testid="cta-final-create"
              className="btn-press inline-flex items-center gap-2 font-medium px-8 py-4 rounded-full text-white"
              style={{ background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)", fontSize: "1.05rem", boxShadow: "0 8px 32px rgba(139,92,246,0.25)" }}>
              {t("final_cta_btn")} <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* Footer */}
      <Footer />

    </div>
  );
}
